import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import LineItemsEditor, { computeTotals } from "@/components/LineItemsEditor";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);

export default function PurchaseInvoiceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeCompanyId, companies, isGstCompany, dateFloor } = useAuth();
  const isEdit = !!id;

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [supplierBill, setSupplierBill] = useState("");
  const [date, setDate] = useState(today());
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState("unpaid");
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentSplits, setPaymentSplits] = useState([]);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    (async () => {
      const [ss, ps, sv] = await Promise.all([api.get("/suppliers"), api.get("/products"), api.get("/services").catch(() => ({ data: [] }))]);
      setSuppliers(ss.data);
      const items = [
        ...ps.data.map((p) => ({ ...p, _kind: "product" })),
        ...(sv.data || []).map((s) => ({
          id: s.id, name: `${s.name} (Service)`, hsn: s.hsn_code || "",
          unit: "SRV", sale_price: s.price || 0, tax_rate: s.tax_rate ?? 18, _kind: "service",
        })),
      ];
      setProducts(items);
      if (isEdit) {
        try {
          const { data } = await api.get(`/purchase-invoices/${id}`);
          setDate(data.date); setDueDate(data.due_date || "");
          setStatus(data.status); setPaidAmount(data.paid_amount || 0);
          setPaymentMode(data.payment_mode || "cash");
          setPaymentReference(data.payment_reference || "");
          setPaymentSplits(Array.isArray(data.payment_splits) ? data.payment_splits : []);
          setNotes(data.notes || ""); setItems(data.items || []);
          setSupplierBill(data.supplier_bill_number || "");
          const s = ss.data.find((x) => x.id === data.supplier_id);
          setSupplier(s || { id: data.supplier_id, name: data.supplier_name, state_code: data.supplier_state_code, gstin: data.supplier_gstin });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
      }
    })();
  }, [activeCompanyId, id, isEdit]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const intraState = supplier && activeCompany && supplier.state_code && activeCompany.state_code && supplier.state_code === activeCompany.state_code;
  const totals = useMemo(() => computeTotals(items, !!intraState, isGstCompany), [items, intraState, isGstCompany]);

  const splitsTotal = useMemo(
    () => (paymentSplits || []).reduce((s, sp) => s + (Number(sp.amount) || 0), 0),
    [paymentSplits],
  );

  const save = async () => {
    if (!supplier) { toast.error("Select a supplier"); return; }
    if (items.length === 0) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      const cleanSplits = (paymentSplits || [])
        .map((sp) => ({
          mode: sp.mode || "cash",
          reference: (sp.reference || "").trim(),
          amount: Number(sp.amount) || 0,
          date: sp.date || date,
        }))
        .filter((sp) => sp.amount > 0);
      const payload = {
        date, due_date: dueDate, status,
        paid_amount: cleanSplits.length ? cleanSplits.reduce((s, sp) => s + sp.amount, 0) : (Number(paidAmount) || 0),
        payment_mode: cleanSplits.length ? cleanSplits[0].mode : paymentMode,
        payment_reference: cleanSplits.length ? cleanSplits[0].reference : paymentReference,
        payment_splits: cleanSplits,
        notes,
        supplier_id: supplier.id, supplier_name: supplier.name,
        supplier_gstin: supplier.gstin || "", supplier_state_code: supplier.state_code || "",
        supplier_bill_number: supplierBill,
        items: items.map((it) => ({
          product_id: it.product_id || "", name: it.name, hsn: it.hsn || "",
          quantity: Number(it.quantity) || 0, unit: it.unit || "PCS",
          rate: Number(it.rate) || 0, discount_pct: Number(it.discount_pct) || 0,
          tax_rate: Number(it.tax_rate) || 0,
        })),
      };
      if (isEdit) {
        await api.patch(`/purchase-invoices/${id}`, payload);
        toast.success("Purchase updated");
      } else {
        await api.post("/purchase-invoices", payload);
        toast.success("Purchase created");
      }
      navigate("/purchases");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="page-shell text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <div>
            <div className="eyebrow">Procurement</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{isEdit ? "Edit Purchase" : "New Purchase Invoice"}</h1>
          </div>
        </div>
      </div>

      <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-1.5 md:col-span-2">
          <Label className="eyebrow">Supplier *</Label>
          <Select value={supplier?.id || ""} onValueChange={(v) => setSupplier(suppliers.find((s) => s.id === v))}>
            <SelectTrigger data-testid="pur-supplier"><SelectValue placeholder="Choose supplier" /></SelectTrigger>
            <SelectContent>
              {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Supplier Bill #</Label>
          <Input value={supplierBill} onChange={(e) => setSupplierBill(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Date</Label>
          <Input type="date" min={dateFloor} value={date} onChange={(e) => setDate(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Due Date</Label>
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-4">
          <Label className="eyebrow">Notes / Work Name</Label>
          <Textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      {/* ── Mode of Payment: log every payment made (cash, UPI, RTGS…) — repeats allowed ── */}
      <div className="card-flat p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <Label className="eyebrow">Mode of Payment</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Log every payment you made against this bill — the same mode may repeat (e.g. ₹500 Cash + ₹1,500 UPI + ₹2,000 RTGS). The supplier ledger creates one Debit Voucher per row, deduped modes show on reports (e.g. <span className="font-mono">CASH · UPI · RTGS</span>).
            </p>
          </div>
          <div className="flex items-center gap-3">
            {paymentSplits.length > 0 && (
              <div className="text-[12px] text-muted-foreground">
                Auto Paid = <span className="font-mono font-semibold text-foreground">{inr(splitsTotal)}</span> · {paymentSplits.length} entr{paymentSplits.length > 1 ? "ies" : "y"}
              </div>
            )}
            <Button type="button" size="sm" variant="outline" className="h-8"
              onClick={() => setPaymentSplits([...(paymentSplits || []), { mode: "cash", reference: "", amount: 0 }])}
              data-testid="pur-add-split">
              + Add Payment
            </Button>
          </div>
        </div>
        {paymentSplits.length === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="eyebrow">Paid Amount (₹)</Label>
              <Input type="number" step="0.01" value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                className="font-mono" data-testid="pur-paid" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Mode (Sent)</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="pur-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="rtgs_neft">RTGS / NEFT</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="debit_card">Debit Card</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">UPI ID / Transaction ID / UTR</Label>
              <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="UPI ID or Transaction ID / UTR No" className="font-mono" data-testid="pur-payment-ref" />
            </div>
          </div>
        )}
        {paymentSplits.length > 0 && (
          <div className="rounded border border-border overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-2.5 py-2 font-semibold w-[24%]">Mode</th>
                  <th className="text-right px-2.5 py-2 font-semibold w-[25%]">Amount (₹)</th>
                  <th className="text-left px-2.5 py-2 font-semibold w-[38%]">UPI ID or Transaction ID / UTR No</th>
                  <th className="px-1 py-2 w-[6%]"></th>
                </tr>
              </thead>
              <tbody>
                {paymentSplits.map((sp, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1.5">
                      <Select value={sp.mode || "cash"} onValueChange={(v) => setPaymentSplits(paymentSplits.map((x, idx) => idx === i ? { ...x, mode: v } : x))}>
                        <SelectTrigger className="h-8" data-testid={`pur-split-${i}-mode`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="rtgs_neft">RTGS / NEFT</SelectItem>
                          <SelectItem value="credit_card">Credit Card</SelectItem>
                          <SelectItem value="debit_card">Debit Card</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" step="0.01" min="0" value={sp.amount || 0}
                        onChange={(e) => setPaymentSplits(paymentSplits.map((x, idx) => idx === i ? { ...x, amount: Number(e.target.value) || 0 } : x))}
                        className="h-8 font-mono text-right" data-testid={`pur-split-${i}-amount`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input value={sp.reference || ""} onChange={(e) => setPaymentSplits(paymentSplits.map((x, idx) => idx === i ? { ...x, reference: e.target.value } : x))}
                        placeholder="UPI ID or Transaction ID / UTR No (optional)" className="h-8 font-mono" data-testid={`pur-split-${i}-ref`} />
                    </td>
                    <td className="px-1 py-1.5 text-right">
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                        onClick={() => setPaymentSplits(paymentSplits.filter((_, idx) => idx !== i))}
                        data-testid={`pur-split-${i}-remove`}>×</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/30">
                  <td className="px-2.5 py-2 text-right eyebrow">Total paid</td>
                  <td className="px-2 py-2 text-right font-mono font-semibold" data-testid="pur-splits-total">{inr(splitsTotal)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <LineItemsEditor items={items} setItems={setItems} products={products} intraState={!!intraState} gstEnabled={isGstCompany} />

      <div className={`grid grid-cols-1 ${isGstCompany ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
        {isGstCompany && (
          <div className="md:col-span-2 card-flat p-6">
            <div className="eyebrow mb-2">Tax Treatment</div>
            <div className="text-sm">
              {intraState ? "Intra-state · CGST + SGST" : "Inter-state · IGST"}
            </div>
            <div className="text-[12px] text-muted-foreground mt-1">
              Company: {activeCompany?.state_code || "—"} · Supplier: {supplier?.state_code || "—"}
            </div>
          </div>
        )}
        {!isGstCompany && (
          <div className="card-flat p-6">
            <div className="eyebrow mb-2">Non-GST Company</div>
            <div className="text-sm text-muted-foreground">This company has no GSTIN registered — purchases are recorded without any GST calculation.</div>
          </div>
        )}
        <div className="card-flat p-6">
          <div className="eyebrow mb-3">Summary</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{isGstCompany ? "Taxable" : "Subtotal"}</span><span className="font-mono">{inr(totals.subtotal)}</span></div>
            {isGstCompany && (intraState ? (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="font-mono">{inr(totals.cgst_total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="font-mono">{inr(totals.sgst_total)}</span></div>
              </>
            ) : (
              <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="font-mono">{inr(totals.igst_total)}</span></div>
            ))}
            <div className="pt-2 border-t border-border flex justify-between font-heading font-bold text-base">
              <span>Grand Total</span><span className="font-mono" data-testid="pur-grand-total">{inr(totals.grand_total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="h-11 px-6 text-base" data-testid="pur-save-btn">
          <FloppyDisk size={18} className="mr-1.5" /> {saving ? "Saving…" : "Save Purchase"}
        </Button>
      </div>
    </div>
  );
}
